/**
 * Bandeja de alertas con evidencia (#42).
 *
 * Tres decisiones que gobiernan esta pantalla:
 *
 * 1. **La evidencia está en el renglón, no sólo en el detalle.** El endpoint
 *    la devuelve en el listado a propósito. Si para saber por qué saltó una
 *    alerta hay que abrirla, revisar cuarenta casos son cuarenta idas y
 *    vueltas y la bandeja se abandona.
 *
 * 2. **Se opera con el teclado.** `j`/`k` para moverse, `a` para tomar el
 *    caso, `c`/`d` para cerrarlo, `?` para la ayuda. La bandeja se usa el
 *    turno entero; el mouse es el cuello de botella.
 *
 * 3. **Un `viewer` lee y no opera.** La API devuelve 403 en `assign` y en
 *    `status`, así que los botones directamente no están: un botón que sólo
 *    puede fallar es peor que ningún botón.
 *
 * El `unit_id` no se renderiza en ningún lado. El caso se identifica por su
 * número y la unidad por el lote.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useApiQuery, useKeysetQuery } from '@/api/hooks';
import { alertKeys } from '@/api/keys';
import type { components } from '@/api/schema';
import { useAuth } from '@/auth/useAuth';
import { useI18n } from '@/i18n/useI18n';
import type { MessageKey } from '@/i18n/messages';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '@/ui/states';
import { AlertDetail } from './AlertDetail';
import { CaseDialog, ShortcutHelp, type CaseDialogMode } from './CaseDialog';
import { EvidenceChips } from './Evidence';
import { evidenceWhy } from './evidence';
import {
  DEFAULT_FILTERS,
  activeFilterCount,
  stableNow,
  toQueryParams,
  type InboxFilters,
} from './filters';
import {
  SHORTCUT_HELP,
  isEditingTarget,
  isWriteAction,
  moveSelection,
  resolveChord,
  type InboxAction,
} from './keyboard';
import {
  SEVERITIES,
  alertTypeLabelKey,
  allowedTransitions,
  isCaseStatus,
  needsResolution,
  severityLabelKey,
  severityTone,
  statusLabelKey,
  statusTone,
  type AlertStatus,
} from './severity';
import { useCaseActions } from './useCaseActions';

type Alert = components['schemas']['AlertOut'];
type AlertSummary = components['schemas']['AlertSummaryOut'];

const PAGE_SIZE = 50;
const SUMMARY_DAYS = 90;

const SHORTCUT_LABELS: Record<InboxAction, MessageKey> = {
  next: 'alerts.shortcut.next',
  previous: 'alerts.shortcut.previous',
  first: 'alerts.shortcut.first',
  last: 'alerts.shortcut.last',
  assignSelf: 'alerts.action.assignSelf',
  unassign: 'alerts.action.unassign',
  investigate: 'alerts.shortcut.investigate',
  confirm: 'alerts.shortcut.confirm',
  dismiss: 'alerts.shortcut.dismiss',
  reopen: 'alerts.shortcut.reopen',
  note: 'alerts.action.note',
  refresh: 'alerts.shortcut.refresh',
  help: 'alerts.shortcut.help',
  close: 'alerts.shortcut.close',
};

export function AlertsInboxPage(): ReactNode {
  const { t, fmt } = useI18n();
  const navigate = useNavigate();
  const params = useParams<{ alertId: string }>();
  const { claims, can } = useAuth();
  const canOperate = can('analyst');

  const [filters, setFilters] = useState<InboxFilters>(DEFAULT_FILTERS);
  const [dialog, setDialog] = useState<CaseDialogMode | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const rowRefs = useRef(new Map<number, HTMLButtonElement>());

  const selectedId = params.alertId === undefined ? null : Number.parseInt(params.alertId, 10);

  // El corte temporal se redondea a la hora: si cambiara en cada render,
  // cambiaría la clave de cache y la lista se pediría de nuevo sin parar.
  const query = useMemo(() => toQueryParams(filters, stableNow(new Date())), [filters]);

  const alerts = useKeysetQuery<Alert>(alertKeys.list(query), '/api/v1/alerts', query, {
    limit: PAGE_SIZE,
  });
  const summary = useApiQuery<AlertSummary>(
    alertKeys.summary(SUMMARY_DAYS),
    '/api/v1/alerts/summary',
    { days: SUMMARY_DAYS },
  );

  const items = alerts.items;
  const selected = items.find((alert) => alert.id === selectedId) ?? null;
  const actions = useCaseActions(selectedId);

  const select = useCallback(
    (alert: Alert | undefined): void => {
      if (alert === undefined) return;
      void navigate(`/alerts/${alert.id}`, { replace: true });
      rowRefs.current.get(alert.id)?.focus();
    },
    [navigate],
  );

  const handleAction = useCallback(
    (action: InboxAction): void => {
      if (action === 'close') {
        setDialog(null);
        setHelpOpen(false);
        return;
      }
      if (action === 'help') {
        setHelpOpen((open) => !open);
        return;
      }
      if (action === 'refresh') {
        void alerts.query.refetch();
        void summary.refetch();
        return;
      }
      if (action === 'next' || action === 'previous' || action === 'first' || action === 'last') {
        const current = items.findIndex((alert) => alert.id === selectedId);
        select(items[moveSelection(current, items.length, action)]);
        return;
      }
      // A partir de acá se escribe. Sin caso elegido no hay sobre qué, y sin
      // rol tampoco: la API contestaría 403.
      if (!canOperate || selected === null) return;

      switch (action) {
        case 'assignSelf':
          if (claims !== null) actions.assign.mutate(claims.userId);
          return;
        case 'unassign':
          actions.assign.mutate(null);
          return;
        case 'note':
          setDialog({ kind: 'note' });
          return;
        case 'investigate':
        case 'confirm':
        case 'dismiss':
        case 'reopen': {
          const target: AlertStatus =
            action === 'investigate'
              ? 'investigating'
              : action === 'confirm'
                ? 'confirmed'
                : action === 'dismiss'
                  ? 'dismissed'
                  : 'open';
          // Una transición que la API no permite devolvería 409: mejor que la
          // tecla no haga nada a que aparezca un error que no explica nada.
          if (!allowedTransitions(selected.status).includes(target)) return;
          if (needsResolution(target)) {
            setDialog({ kind: 'status', status: target });
          } else {
            actions.changeStatus.mutate({ status: target, resolution: null, note: null });
          }
          return;
        }
      }
    },
    [actions, alerts.query, canOperate, claims, items, select, selected, selectedId, summary],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const action = resolveChord({
        key: event.key,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        altKey: event.altKey,
        editing: isEditingTarget(event.target),
      });
      if (action === null) return;
      if (isWriteAction(action) && !canOperate) return;
      event.preventDefault();
      handleAction(action);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => { window.removeEventListener('keydown', onKeyDown); };
  }, [handleAction, canOperate]);

  const shortcutRows = useMemo(
    () =>
      SHORTCUT_HELP.filter((row) => canOperate || !isWriteAction(row.action)).map((row) => ({
        keys: row.keys,
        label: t(SHORTCUT_LABELS[row.action]),
      })),
    [canOperate, t],
  );

  return (
    <div className="mx-auto max-w-[110rem] space-y-4">
      {/* ─────────────────────────────────────────────────── contadores ── */}
      <Card>
        <CardHeader
          title={t('alerts.inbox.title')}
          description={t('alerts.inbox.description')}
          actions={
            <Button size="sm" variant="ghost" onClick={() => { setHelpOpen(true); }}>
              {t('alerts.shortcuts.open')}
            </Button>
          }
        />
        <CardBody className="flex flex-wrap items-center gap-x-6 gap-y-2">
          {summary.isPending && <p className="text-sm text-ink-subtle">{t('state.loading')}</p>}
          {summary.isError && <ErrorState error={summary.error} />}
          {summary.data !== undefined && (
            <>
              <Counter label={t('alerts.summary.open')} value={fmt.number(summary.data.open_total)} />
              <Counter
                label={t('alerts.summary.unassigned')}
                value={fmt.number(summary.data.unassigned_total)}
              />
              <Counter label={t('alerts.summary.mine')} value={fmt.number(summary.data.assigned_to_me)} />
              <ul className="flex flex-wrap gap-2">
                {summary.data.by_severity.map((bucket) => {
                  const key = severityLabelKey(bucket.severity);
                  return (
                    <li key={bucket.severity}>
                      <Badge tone={severityTone(bucket.severity)}>
                        {key === null ? bucket.severity_label : t(key)}
                        <span className="font-mono">{fmt.number(bucket.total)}</span>
                      </Badge>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </CardBody>

        {/* ──────────────────────────────────────────────────── filtros ── */}
        <CardBody className="flex flex-wrap items-end gap-3 border-t border-border">
          <Select
            label={t('alerts.filter.status')}
            value={filters.status ?? ''}
            onChange={(value) => {
              setFilters((current) => ({
                ...current,
                status: isCaseStatus(value) ? value : null,
              }));
            }}
            options={[
              { value: '', label: t('alerts.filter.any') },
              { value: 'open', label: t('alerts.status.open') },
              { value: 'investigating', label: t('alerts.status.investigating') },
              { value: 'confirmed', label: t('alerts.status.confirmed') },
              { value: 'dismissed', label: t('alerts.status.dismissed') },
            ]}
          />
          <Select
            label={t('alerts.filter.severity')}
            value={filters.severityMin === null ? '' : String(filters.severityMin)}
            onChange={(value) => {
              setFilters((current) => ({
                ...current,
                severityMin: value === '' ? null : Number.parseInt(value, 10),
              }));
            }}
            options={[
              { value: '', label: t('alerts.filter.any') },
              ...SEVERITIES.map((severity) => {
                const key = severityLabelKey(severity);
                return {
                  value: String(severity),
                  label: t('alerts.filter.severityFrom', {
                    severity: key === null ? severity : t(key),
                  }),
                };
              }),
            ]}
          />
          <Select
            label={t('alerts.filter.type')}
            value={filters.alertType ?? ''}
            onChange={(value) => {
              setFilters((current) => ({ ...current, alertType: value === '' ? null : value }));
            }}
            options={[
              { value: '', label: t('alerts.filter.any') },
              ...(summary.data?.by_type ?? []).map((bucket) => {
                const key = alertTypeLabelKey(bucket.alert_type);
                return {
                  value: bucket.alert_type,
                  label: `${key === null ? bucket.alert_type : t(key)} (${fmt.number(bucket.total)})`,
                };
              }),
            ]}
          />
          <Select
            label={t('alerts.filter.assignment')}
            value={filters.assignment}
            onChange={(value) => {
              setFilters((current) => ({
                ...current,
                assignment:
                  value === 'mine' ? 'mine' : value === 'unassigned' ? 'unassigned' : 'all',
              }));
            }}
            options={[
              { value: 'all', label: t('alerts.filter.any') },
              { value: 'mine', label: t('alerts.filter.mine') },
              { value: 'unassigned', label: t('alerts.filter.unassigned') },
            ]}
          />
          <Select
            label={t('alerts.filter.sort')}
            value={filters.sort}
            onChange={(value) => {
              setFilters((current) => ({
                ...current,
                sort: value === 'recent' ? 'recent' : 'severity',
              }));
            }}
            options={[
              { value: 'severity', label: t('alerts.filter.sortSeverity') },
              { value: 'recent', label: t('alerts.filter.sortRecent') },
            ]}
          />
          <Select
            label={t('alerts.filter.window')}
            value={filters.sinceDays === null ? '' : String(filters.sinceDays)}
            onChange={(value) => {
              setFilters((current) => ({
                ...current,
                sinceDays: value === '' ? null : Number.parseInt(value, 10),
              }));
            }}
            options={[
              { value: '1', label: t('alerts.filter.window1') },
              { value: '7', label: t('alerts.filter.window7') },
              { value: '30', label: t('alerts.filter.window30') },
              { value: '90', label: t('alerts.filter.window90') },
              { value: '', label: t('alerts.filter.windowAll') },
            ]}
          />
          {activeFilterCount(filters) > 0 && (
            <Button size="sm" variant="ghost" onClick={() => { setFilters(DEFAULT_FILTERS); }}>
              {t('alerts.filter.reset')}
            </Button>
          )}
        </CardBody>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        {/* ───────────────────────────────────────────────── la bandeja ── */}
        <Card>
          <CardHeader
            title={t('alerts.list.title')}
            description={t('alerts.list.description')}
            actions={
              items.length > 0 ? (
                <Badge tone="neutral">{t('alerts.list.count', { count: fmt.number(items.length) })}</Badge>
              ) : undefined
            }
          />

          {alerts.query.isPending && <LoadingState rows={6} />}

          {alerts.query.isError && (
            <CardBody>
              <ErrorState
                error={alerts.query.error}
                onRetry={() => { void alerts.query.refetch(); }}
              />
            </CardBody>
          )}

          {!alerts.query.isPending && !alerts.query.isError && items.length === 0 && (
            <EmptyState title={t('alerts.list.empty')} detail={t('alerts.list.emptyHint')} />
          )}

          {items.length > 0 && (
            <>
              <ul className="divide-y divide-border">
                {items.map((alert) => (
                  <li key={alert.id}>
                    <AlertRow
                      alert={alert}
                      selected={alert.id === selectedId}
                      onSelect={() => { select(alert); }}
                      registerRef={(element) => {
                        if (element === null) rowRefs.current.delete(alert.id);
                        else rowRefs.current.set(alert.id, element);
                      }}
                    />
                  </li>
                ))}
              </ul>

              <CardBody className="flex items-center justify-center border-t border-border">
                {alerts.hasMore ? (
                  <Button size="sm" onClick={alerts.loadMore} disabled={alerts.isLoadingMore}>
                    {alerts.isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
                  </Button>
                ) : (
                  <p className="text-xs text-ink-subtle">{t('state.allLoaded')}</p>
                )}
              </CardBody>
            </>
          )}
        </Card>

        {/* ──────────────────────────────────────────────────── el caso ── */}
        <Card className="self-start">
          {selectedId === null || Number.isNaN(selectedId) ? (
            <EmptyState title={t('alerts.detail.none')} detail={t('alerts.detail.noneHint')} />
          ) : (
            <AlertDetail
              alertId={selectedId}
              actions={actions}
              canOperate={canOperate}
              onRequestStatus={(status) => { setDialog({ kind: 'status', status }); }}
              onRequestNote={() => { setDialog({ kind: 'note' }); }}
            />
          )}
        </Card>
      </div>

      {dialog !== null && (
        <CaseDialog
          mode={dialog}
          isPending={actions.isPending}
          onCancel={() => { setDialog(null); }}
          onSubmit={(input) => {
            if (dialog.kind === 'note') {
              if (input.note === null) return;
              actions.addNote.mutate(input.note, { onSuccess: () => { setDialog(null); } });
              return;
            }
            actions.changeStatus.mutate(
              { status: dialog.status, resolution: input.resolution, note: input.note },
              { onSuccess: () => { setDialog(null); } },
            );
          }}
        />
      )}

      {helpOpen && <ShortcutHelp rows={shortcutRows} onClose={() => { setHelpOpen(false); }} />}
    </div>
  );
}

function Counter({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <p className="text-xs tracking-wide text-ink-subtle uppercase">{label}</p>
      <p className="font-mono text-lg font-semibold text-ink">{value}</p>
    </div>
  );
}

function Select({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
}): ReactNode {
  return (
    <label className="block">
      <span className="text-xs tracking-wide text-ink-subtle uppercase">{label}</span>
      <select
        className="mt-1 h-9 w-full rounded border border-border-strong bg-surface px-2 text-sm text-ink"
        value={value}
        onChange={(event) => { onChange(event.target.value); }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Un renglón de la bandeja. Lleva la evidencia: el porqué de la regla y los
 * dos o tres campos que deciden si esto se atiende ahora o no.
 */
function AlertRow({
  alert,
  selected,
  onSelect,
  registerRef,
}: {
  alert: Alert;
  selected: boolean;
  onSelect: () => void;
  registerRef: (element: HTMLButtonElement | null) => void;
}): ReactNode {
  const { t, fmt } = useI18n();
  const typeKey = alertTypeLabelKey(alert.alert_type);
  const severityKey = severityLabelKey(alert.severity);
  const statusKey = statusLabelKey(alert.status);
  const why = evidenceWhy(alert.evidence);

  return (
    <button
      type="button"
      ref={registerRef}
      onClick={onSelect}
      aria-current={selected}
      className={`block w-full px-4 py-3 text-left transition-colors ${
        selected ? 'bg-accent-soft' : 'hover:bg-surface-sunken'
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={severityTone(alert.severity)}>
          {severityKey === null ? alert.severity_label : t(severityKey)}
        </Badge>
        <span className="font-medium text-ink">
          {typeKey === null ? alert.alert_type : t(typeKey)}
        </span>
        <Badge tone={statusTone(alert.status)}>
          {statusKey === null ? alert.status : t(statusKey)}
        </Badge>
        <span className="ml-auto font-mono text-xs text-ink-subtle">
          {t('alerts.case', { id: alert.id })}
        </span>
      </div>

      {why !== null && <p className="mt-1 line-clamp-2 text-sm text-ink-muted">{why}</p>}

      <div className="mt-1">
        <EvidenceChips alertType={alert.alert_type} evidence={alert.evidence} />
      </div>

      <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-subtle">
        <span>{fmt.relative(alert.created_at)}</span>
        {alert.lot_code !== null && alert.lot_code !== undefined && (
          <span>
            {t('alerts.column.batch')}: {alert.lot_code}
          </span>
        )}
        <span>
          {alert.assignee_name ?? alert.assignee_email ?? t('alerts.unassigned')}
        </span>
      </div>
    </button>
  );
}
